import "rc-slider/assets/index.css";
import "./TimelineWorksheet.scss";

import { format } from "date-fns";
import { cloneDeep } from "lodash-es";
import Slider from "rc-slider";
import { useEffect, useState } from "react";

import type { RevMetaType, RevType } from "../../shared/types/sharedb";
import type { WorksheetType } from "../../shared/types/worksheet";
import { jsonOTType } from "../../shared/worksheets/ot-types/ot-patch";

import { getRevs } from "api/api-worksheets";
import { t } from "i18n/i18n";
import { useAppSelector } from "store/index";
import { selectCurrentSectionId, selectCurrentUserId, userSelectors } from "store/selectors";
import { toastLocalizedOperationError } from "utils/alerts";
import { getUserFullName } from "utils/user-utils";
import { getFakeDoc } from "worksheets/helpers/fake-doc";
import { Worksheet } from "worksheets/views/Worksheet";

/** Reconstructed worksheet doc at a point in time, plus ShareDB op metadata for that point. */
type WorksheetSnapshot = [WorksheetType, RevMetaType];

type TimelineWorksheetDebug = {
  snapshotIndex: number;
  /** ShareDB edit ops after document create (does not include the create op). */
  editRevisions: RevType[];
  /** Doc state at each slider position; index 0 is the create snapshot. */
  snapshots: WorksheetSnapshot[];
  currentSnapshot: { doc: WorksheetType; meta: RevMetaType };
  /** Op that produced the current snapshot; null at index 0 (create). */
  editThatProducedCurrentView: RevType | null;
  /** Op from the current snapshot to the next; null on the last snapshot. */
  editToNextSnapshot: RevType | null;
};

declare global {
  interface Window {
    timelineWorksheet?: TimelineWorksheetDebug;
  }
}

const buildSnapshots = (createRevision: RevType, editRevisions: RevType[]): WorksheetSnapshot[] => {
  const createData = createRevision.create?.data;
  if (!createData) {
    return [];
  }

  return editRevisions.reduce(
    (snapshots, editRevision) => {
      const previousDoc = snapshots[snapshots.length - 1][0];
      const doc = jsonOTType.type.apply(cloneDeep(previousDoc), editRevision.op) as WorksheetType;
      return [...snapshots, [doc, editRevision.m]];
    },
    [[createData, createRevision.m] satisfies WorksheetSnapshot],
  );
};

export const TimelineWorksheet = ({ docId }: { docId: string }) => {
  const [snapshotIndex, setSnapshotIndex] = useState(0);
  const [snapshots, setSnapshots] = useState<WorksheetSnapshot[]>([]);
  const [dayMarks, setDayMarks] = useState<Record<string, string>>({});
  const [editRevisions, setEditRevisions] = useState<RevType[] | null>(null);
  const currentUserId = useAppSelector(selectCurrentUserId);
  const currentSectionId = useAppSelector(selectCurrentSectionId);

  const users = useAppSelector(userSelectors.selectEntities);

  useEffect(() => {
    if (!currentSectionId) {
      return;
    }

    const processRevisions = (allRevisions: RevType[]) => {
      const [createRevision, ...editsAfterCreate] = allRevisions;
      const builtSnapshots = createRevision ? buildSnapshots(createRevision, editsAfterCreate) : [];

      if (builtSnapshots.length === 0) {
        return;
      }

      setEditRevisions(editsAfterCreate);
      setSnapshots(builtSnapshots);
      setSnapshotIndex(0);

      let previousDay: number | null = null;
      const snapshotDayMarks = builtSnapshots.reduce(
        (marks, [, meta]: WorksheetSnapshot, index) => {
          const day = Math.floor(meta.ts / (1000 * 60 * 60 * 24));
          const dateName = new Date(meta.ts).toDateString().split(" ").slice(1, 3).join(" ");

          if (day !== previousDay) {
            previousDay = day;
            return { ...marks, [index]: dateName };
          }

          return marks;
        },
        {} as Record<number, string>,
      );

      setDayMarks(snapshotDayMarks);
    };

    getRevs(docId, currentSectionId)
      .then((response) => processRevisions(response.data))
      .catch(() => toastLocalizedOperationError("failed_to_retrieve_revisions"));
  }, [docId, currentSectionId]);

  if (editRevisions === null) {
    return (
      <div className="mt-4 flex items-center justify-center">{t("common.loading_ellipsis")}</div>
    );
  }

  if (snapshots.length === 0) {
    return (
      <div className="mt-4 flex items-center justify-center">
        {t("worksheet.timeline.no_revisions")}
      </div>
    );
  }

  const [doc, meta] = snapshots[snapshotIndex];
  const modifyDate = new Date(meta.ts);

  const timezone = new Intl.DateTimeFormat(undefined, { timeZoneName: "short" })
    .formatToParts(modifyDate)
    ?.find((part) => part.type === "timeZoneName")?.value;

  const modifyDateString = `${format(modifyDate, "MMM dd, yyyy - hh:mm:ss a")}${timezone ? ` (${timezone})` : ""}`;
  const user = users[meta.userId] || null;
  const username = getUserFullName(user) || t("user_info.anonymous_user");
  const revisionLabel =
    user?.id === currentUserId
      ? t("worksheet.timeline.revision_label_current_user", { date: modifyDateString })
      : t("worksheet.timeline.revision_label", { date: modifyDateString, userName: username });

  const skeletonDocWithHistoricData = getFakeDoc(doc);

  window.timelineWorksheet = {
    snapshotIndex,
    editRevisions,
    snapshots,
    currentSnapshot: { doc, meta },
    editThatProducedCurrentView:
      snapshotIndex > 0 ? (editRevisions[snapshotIndex - 1] ?? null) : null,
    editToNextSnapshot:
      snapshotIndex < editRevisions.length ? (editRevisions[snapshotIndex] ?? null) : null,
  };

  return (
    <div className="flex w-full flex-col items-center">
      {/* TODO: Preferably this slider would be pinned to the top of the page, and sliding
      it would scroll the page to the relevant change, which would require inspecting each
      op and jumping to the corresponding block component that renders it. */}
      <div className="extended-page-block-width body px-5 pt-5">
        <h2 className="h2">{t("worksheet.timeline.title")}</h2>

        <div>{t("worksheet.timeline.description")}</div>

        <div className="body mb-2 mt-5">{revisionLabel}</div>

        <div className="mx-4 pb-4">
          <Slider
            marks={dayMarks}
            max={snapshots.length - 1}
            min={0}
            value={snapshotIndex}
            onChange={(index: number) => setSnapshotIndex(index)}
          />
        </div>

        <div className="mb-2 mt-5 w-full border-b border-black-tint-70" />
      </div>

      <Worksheet doc={skeletonDocWithHistoricData} />
    </div>
  );
};
